import { Tooltip } from 'antd'
import classNames from 'classnames'
import { MyForm } from '~/components/ui/MyForm'
import styles from '../AiForm.module.css'
import { QuestionCircleOutlined } from '@ant-design/icons'
import useOptions from './useOptions'
import { useAtom } from 'jotai'
import {
  cfgAtom,
  clipSkipAtom,
  ensdAtom,
  modelCodeAtom,
  resolutionKeyAtom,
  selectModelAtom,
  selectSamplerAtom,
} from './atom'
import { useEffect, useState } from 'react'
import { isNullOrUndefined, uniqueFunc } from '~/utils'
import { AiCreateArtworkModel } from '~/graphqls/types'
import { MyBtn } from '~/components/ui/MyBtn'
import { OptionsTabs } from '../OptionsTabs'
import { MyModal } from '~/components/ui/MyModal'
import { MyIcon } from '~/components/ui/MyIcon'

/** 默认显示数量 */
const DEFAULT_SHOW_COUNT = 14

interface ModelThemeOptionsProps {
  name: string
  url: string
  isNew: boolean | null | undefined
  modelCode: number
  category: string
  model: AiCreateArtworkModel
  cfg?: number | undefined | null
  samplerIndex?: number | undefined | null
  resolutionKey?: string | undefined | null
  ensd?: number | undefined | null
  clipSkip?: number | undefined | null
}

const ModelThemeItem: React.FC<{
  item: ModelThemeOptionsProps
  onChecked: () => void
  isActive: boolean
}> = ({ item, onChecked, isActive }) => {
  return (
    <div
      data-model-code={item.modelCode}
      style={{
        background: `url(${item.url}) no-repeat center center/cover`,
        width: 'calc((100% - 48px) / 5)',
      }}
      onClick={onChecked}
      className="h-16 mb-3 rounded-lg overflow-hidden cursor-pointer relative"
    >
      {item.isNew && (
        <img
          className="h-[14px] absolute top-1 right-1"
          src="/images/ai/new.png"
          alt=""
        />
      )}
      <div
        className={classNames('h-16 rounded-lg overflow-hidden', {
          '!border-neutral-800 !border-2 !border-solid': isActive,
        })}
      >
        <div
          className="w-full h-full flex items-center justify-center"
          style={{
            background:
              'linear-gradient(180deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.5) 100%)',
          }}
        >
          <div className="text-neutral-800 font-semibold select-none">
            {item.name}
          </div>
        </div>
      </div>
    </div>
  )
}

export const ModelThemeComp = () => {
  const [selectModel, setSelectModel] = useAtom(selectModelAtom)
  const [modelCode, setModelCode] = useAtom(modelCodeAtom)
  const [, setResolutionKey] = useAtom(resolutionKeyAtom)
  const [, setSelectSampler] = useAtom(selectSamplerAtom)
  const [, setCfg] = useAtom(cfgAtom)
  const [, setEnsd] = useAtom(ensdAtom)
  const [, setClipSkip] = useAtom(clipSkipAtom)

  const [visible, setVisible] = useState(false)
  const [, setCurrentIndex] = useState(0)

  const { modelThemeOptions, curModelThemeOptions } = useOptions()

  const tabs = uniqueFunc(
    modelThemeOptions
      ?.filter(item => !!item.category)
      ?.map(item => ({
        name: item.category,
        model: item.model,
      })),
    'model'
  )

  /** 特殊的交互，和角色同人等不同，选中的不置顶 */
  const defaultOptions = uniqueFunc(
    [
      ...curModelThemeOptions?.slice(0, DEFAULT_SHOW_COUNT),
      ...curModelThemeOptions.filter(o => o.modelCode === modelCode),
    ],
    'name'
  )

  const handleClickOption = (item: ModelThemeOptionsProps) => {
    setModelCode(item.modelCode)
    setResolutionKey(item.resolutionKey!)
    setCfg(item.cfg ?? undefined)
    setSelectSampler(
      isNullOrUndefined(item.samplerIndex) ? undefined : [item.samplerIndex!]
    )
    setEnsd(item.ensd ?? undefined)
    setClipSkip(item.clipSkip ?? undefined)
  }

  /** 默认选中第一个模型主题 */
  useEffect(() => {
    if (
      curModelThemeOptions.findIndex(item => item.modelCode === modelCode) ===
      -1
    ) {
      setModelCode(curModelThemeOptions?.[0]?.modelCode!)
      setResolutionKey(curModelThemeOptions?.[0]?.resolutionKey!)

      setCfg(curModelThemeOptions?.[0]?.cfg ?? undefined)
      setSelectSampler(
        isNullOrUndefined(curModelThemeOptions?.[0]?.samplerIndex)
          ? undefined
          : [curModelThemeOptions?.[0]?.samplerIndex!]
      )
      setEnsd(curModelThemeOptions?.[0]?.ensd ?? undefined)
      setClipSkip(curModelThemeOptions?.[0]?.clipSkip ?? undefined)
    }
  }, [curModelThemeOptions, modelCode])

  return (
    <>
      <MyForm.Item
        label={
          <span>
            <span className="text-neutral-500 font-[SimSun]">* 模型主题 </span>
            <Tooltip
              placement="topLeft"
              arrowPointAtCenter
              title="通过模型主题设定，可以设定最终生成出来的画面是什么材质和风格"
            >
              <QuestionCircleOutlined />
            </Tooltip>
          </span>
        }
        className={classNames(styles.borderLessFormItem, '!mb-5')}
      >
        <div className="flex flex-wrap gap-3">
          {defaultOptions.map(item => {
            const isActive = modelCode === item.modelCode
            return (
              <ModelThemeItem
                key={item.name}
                isActive={isActive}
                item={item}
                onChecked={() => handleClickOption(item)}
              />
            )
          })}
          <div
            className={classNames(
              'h-16 mb-3 rounded-lg text-neutral-800 font-semibold border-2 border-neutral-300 cursor-pointer overflow-hidden flex items-center justify-center'
            )}
            style={{
              background: 'rgba(35, 38, 47, 0.32)',
              width: 'calc((100% - 48px) / 5)',
            }}
            onClick={() => setVisible(true)}
          >
            <MyIcon
              className="text-[14px] leading-[16px] font-normal"
              icon={'more1'}
            />
            <span className="ml-0.5">更多</span>
          </div>
        </div>
      </MyForm.Item>
      <MyModal
        open={visible}
        title={<>模型主题</>}
        onCancel={() => setVisible(false)}
        maskStyle={{
          backgroundColor: 'rgba(0,0,0,0.8)',
        }}
        dark
        width={720}
        footer={null}
      >
        <OptionsTabs
          options={tabs.map(item => item.name)}
          currentIndex={tabs.findIndex(item => item.model === selectModel)}
          setCurrentIndex={setCurrentIndex}
          onChange={index => setSelectModel(tabs[index].model)}
        />
        <div className="flex flex-wrap gap-3">
          {curModelThemeOptions.map(item => {
            const isActive = modelCode === item.modelCode
            return (
              <ModelThemeItem
                key={item.name}
                isActive={isActive}
                item={item}
                onChecked={() => handleClickOption(item)}
              />
            )
          })}
        </div>
        <div className="flex justify-between items-center gap-3">
          <MyBtn
            block
            type="default"
            className="!bg-neutral-200 !border-neutral-200 !text-neutral-800"
            onClick={() => {
              setVisible(false)
            }}
          >
            取消
          </MyBtn>
          <MyBtn block type="primary" onClick={() => setVisible(false)}>
            确定
          </MyBtn>
        </div>
      </MyModal>
    </>
  )
}
